import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Card,
  CardBody,
  Heading,
  useColorModeValue,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Alert,
  Select,
  Tooltip,
  Link,
  Input,
  Progress,
} from '@chakra-ui/react'
import { DeleteIcon, RepeatIcon, ChevronLeftIcon, ChevronRightIcon, AttachmentIcon } from '@chakra-ui/icons'

interface ValidationResult {
  id: number
  question_set_name: string
  result_file: string
  total_questions: number
  none_count: number            // 정상
  no_answer_count: number       // 정보없음
  format_count: number          // 출력형식
  irrelevant_count: number      // 관련성없음
  wrong_count: number           // 오답
  communication_count?: number  // 통신오류 (HTTP 에러 등)
  avg_confidence: number        // 평균 신뢰도
  avg_response_time: number
  is_random: boolean
  created_at: string
}

interface QuestionPoolStats {
  total: number
  active: number
  categories: number
  byCategory: { category: string; count: number }[]
}

const ValidationReportPage: React.FC = () => {
  // 상태
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([])
  const [poolStats, setPoolStats] = useState<QuestionPoolStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [selectedCount, setSelectedCount] = useState(50)
  const [selectedCategory, setSelectedCategory] = useState('전체')
  
  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  
  // 날짜 검색 상태
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  
  // 파일 업로드 상태
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { isOpen: isUploadModalOpen, onOpen: onUploadModalOpen, onClose: onUploadModalClose } = useDisclosure()
  const [uploadPreview, setUploadPreview] = useState<{ total: number; sample: any[] } | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  
  // 🆕 커스텀 등록 날짜 상태 (12월 소급 등록용)
  const [customCreatedAt, setCustomCreatedAt] = useState('')

  const toast = useToast()
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // 데이터 로드 (페이징 및 날짜 필터 포함)
  const loadValidationResults = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      })
      
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      
      const response = await fetch(`/api/validation/results-v2?${params.toString()}`)
      const data = await response.json()
      if (data.success) {
        setValidationResults(data.data || [])
        setTotalPages(data.pagination?.totalPages || 1)
        setTotalItems(data.pagination?.totalItems || data.data?.length || 0)
      }
    } catch (error) {
      console.error('검증 결과 로드 오류:', error)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, pageSize, startDate, endDate])

  const loadPoolStats = useCallback(async () => {
    try {
      const response = await fetch('/api/validation/question-pool/stats')
      const data = await response.json()
      if (data.success) {
        setPoolStats(data.data)
      }
    } catch (error) {
      console.error('질문 풀 통계 로드 오류:', error)
    }
  }, [])

  useEffect(() => {
    loadValidationResults()
  }, [loadValidationResults])

  useEffect(() => {
    loadPoolStats()
  }, [loadPoolStats])

  // 랜덤 검증 실행
  const handleRunRandomValidation = async () => {
    if (!poolStats || poolStats.active === 0) {
      toast({
        title: '질문 풀이 비어 있습니다',
        description: '먼저 질문을 추가해주세요.',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    setIsRunning(true)

    try {
      const response = await fetch('/api/validation/run-random', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          count: selectedCount,
          category: selectedCategory === '전체' ? null : selectedCategory,
          customCreatedAt: customCreatedAt || null  // 🆕 커스텀 등록 날짜
        })
      })

      const data = await response.json()
      
      if (data.success) {
        const dateInfo = customCreatedAt ? ` (등록일: ${customCreatedAt})` : ''
        toast({
          title: '검증 완료',
          description: `리포트가 생성되었습니다: ${data.data.reportFile}${dateInfo}`,
          status: 'success',
          duration: 5000,
        })
        setCurrentPage(1)  // 첫 페이지로 이동
        setCustomCreatedAt('')  // 등록 날짜 초기화
        loadValidationResults()
        
        // 결과를 새 탭으로 열기
        window.open(`/results/${data.data.reportFile}`, '_blank')
      } else {
        throw new Error(data.message)
      }
    } catch (error: any) {
      toast({
        title: '검증 오류',
        description: error.message || '검증 실행에 실패했습니다.',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsRunning(false)
    }
  }

  // 결과 삭제
  const handleDeleteResult = async (id: number) => {
    if (!window.confirm('검증 결과를 삭제하시겠습니까?')) return

    try {
      const response = await fetch(`/api/validation/results/${id}`, {
        method: 'DELETE'
      })
      const data = await response.json()
      if (data.success) {
        toast({
          title: '삭제 완료',
          status: 'success',
          duration: 2000,
        })
        loadValidationResults()
      }
    } catch (error) {
      toast({
        title: '오류',
        description: '삭제에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // JSON 파일 선택 핸들러
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.json')) {
      toast({
        title: '파일 형식 오류',
        description: 'JSON 파일만 업로드 가능합니다.',
        status: 'error',
        duration: 3000,
      })
      return
    }

    try {
      const text = await file.text()
      const json = JSON.parse(text)
      
      // questions 배열 확인
      const questions = json.questions || json
      if (!Array.isArray(questions)) {
        throw new Error('questions 배열을 찾을 수 없습니다.')
      }

      setUploadFile(file)
      setUploadPreview({
        total: questions.length,
        sample: questions.slice(0, 5)
      })
      onUploadModalOpen()
    } catch (error: any) {
      toast({
        title: '파일 파싱 오류',
        description: error.message || 'JSON 파일을 파싱할 수 없습니다.',
        status: 'error',
        duration: 3000,
      })
    }

    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // JSON 파일 업로드 실행
  const handleUploadConfirm = async () => {
    if (!uploadFile) return

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const text = await uploadFile.text()
      const json = JSON.parse(text)
      const questions = json.questions || json

      // 질문 변환 (JSON 형식을 API 형식으로)
      const formattedQuestions = questions.map((q: any) => ({
        question: q.question,
        expected_answer: q.expected_answer || null,
        category: q.category || '미분류',
        question_type: q.question_type || 'factual',
        difficulty: q.difficulty || 'medium',
        source: q.source || uploadFile.name
      }))

      // 배치로 분할 업로드 (100개씩)
      const batchSize = 100
      let totalInserted = 0

      for (let i = 0; i < formattedQuestions.length; i += batchSize) {
        const batch = formattedQuestions.slice(i, i + batchSize)
        
        const response = await fetch('/api/validation/question-pool/bulk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ questions: batch })
        })

        const data = await response.json()
        if (!data.success) {
          throw new Error(data.message)
        }

        totalInserted += data.data?.inserted || batch.length
        setUploadProgress(Math.round(((i + batch.length) / formattedQuestions.length) * 100))
      }

      toast({
        title: '업로드 완료',
        description: `${totalInserted}개의 질문이 추가되었습니다.`,
        status: 'success',
        duration: 5000,
      })

      onUploadModalClose()
      setUploadFile(null)
      setUploadPreview(null)
      loadPoolStats()
    } catch (error: any) {
      toast({
        title: '업로드 오류',
        description: error.message || '질문 업로드에 실패했습니다.',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  // 날짜 포맷
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // 날짜 검색 핸들러
  const handleDateSearch = () => {
    setCurrentPage(1)
    loadValidationResults()
  }

  // 날짜 초기화
  const handleResetDate = () => {
    setStartDate('')
    setEndDate('')
    setCurrentPage(1)
  }

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // 페이지 번호 계산 (최대 5개 표시)
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxPagesToShow = 5
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      let startPage = Math.max(1, currentPage - 2)
      let endPage = Math.min(totalPages, currentPage + 2)
      
      if (startPage > 1) {
        pages.push(1)
        if (startPage > 2) {
          pages.push('...')
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...')
        }
        pages.push(totalPages)
      }
    }
    
    return pages
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* 헤더 */}
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="bexco.700">📊 검증 리포트</Heading>
            <Text color="gray.500" mt={1}>RAG 챗봇 응답 품질 검증</Text>
          </Box>
          
          {/* 랜덤 검증 컨트롤 */}
          <HStack spacing={3} flexWrap="wrap">
            {/* 파일 업로드 버튼 */}
            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              leftIcon={<AttachmentIcon />}
              variant="outline"
              colorScheme="purple"
              onClick={() => fileInputRef.current?.click()}
            >
              질문 업로드
            </Button>
            
            <Select 
              value={selectedCount} 
              onChange={(e) => setSelectedCount(parseInt(e.target.value))}
              width="120px"
              bg="white"
            >
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
              <option value={200}>200개</option>
              <option value={500}>500개</option>
            </Select>
            
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              width="150px"
              bg="white"
            >
              <option value="전체">전체 카테고리</option>
              {poolStats?.byCategory
                .slice()
                .sort((a, b) => a.category.localeCompare(b.category, 'ko'))
                .map(cat => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category} ({cat.count})
                  </option>
                ))}
            </Select>
            
            {/* 🆕 등록 날짜 선택 (소급 등록용) */}
            <Tooltip label="등록 날짜 지정 (비우면 오늘 날짜)">
              <Input
                type="date"
                value={customCreatedAt}
                onChange={(e) => setCustomCreatedAt(e.target.value)}
                placeholder="등록날짜"
                width="150px"
                bg="white"
                max={new Date().toISOString().split('T')[0]}
              />
            </Tooltip>
            
            <Button
              colorScheme="green"
              onClick={handleRunRandomValidation}
              isLoading={isRunning}
              loadingText="검증 중..."
              isDisabled={isRunning || !poolStats || poolStats.active === 0}
            >
              검증
            </Button>
          </HStack>
        </HStack>

        {/* 질문 풀 통계 */}
        {poolStats && (
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody py={4}>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Stat size="sm" textAlign="center">
                  <StatLabel>질문 풀</StatLabel>
                  <StatNumber color="blue.500">{poolStats.active.toLocaleString()}개</StatNumber>
                </Stat>
                <Stat size="sm" textAlign="center">
                  <StatLabel>카테고리</StatLabel>
                  <StatNumber color="purple.500">{poolStats.categories}개</StatNumber>
                </Stat>
                <Stat size="sm" textAlign="center">
                  <StatLabel>선택된 질문 수</StatLabel>
                  <StatNumber color="green.500">{selectedCount}개</StatNumber>
                </Stat>
                <Stat size="sm" textAlign="center">
                  <StatLabel>예상 소요시간</StatLabel>
                  <StatNumber color="orange.500">{Math.ceil(selectedCount * 1.5)}초</StatNumber>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* 검증 진행 중 표시 */}
        {isRunning && (
          <Alert status="info" borderRadius="md">
            <Spinner size="sm" mr={3} />
            <Box>
              <Text fontWeight="bold">검증 진행 중...</Text>
              <Text fontSize="sm">{selectedCount}개 질문 검증 중 - 약 {Math.ceil(selectedCount * 1.5)}초 소요 예상</Text>
            </Box>
          </Alert>
        )}

        {/* 검증 결과 목록 */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
              <Heading size="md">📋 검증 결과</Heading>
              
              {/* 날짜 검색 */}
              <HStack spacing={2} flexWrap="wrap">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="sm"
                  width="140px"
                  bg="white"
                />
                <Text fontSize="sm" color="gray.500">~</Text>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="sm"
                  width="140px"
                  bg="white"
                />
                <Button size="sm" colorScheme="blue" onClick={handleDateSearch}>
                  검색
                </Button>
                {(startDate || endDate) && (
                  <Button size="sm" variant="outline" onClick={handleResetDate}>
                    초기화
                  </Button>
                )}
                <IconButton
                  aria-label="새로고침"
                  icon={<RepeatIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={loadValidationResults}
                />
              </HStack>
            </HStack>

            {isLoading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="lg" color="bexco.500" />
              </Box>
            ) : validationResults.length === 0 ? (
              <Box textAlign="center" py={10} color="gray.500">
                <Text>검증 결과가 없습니다.</Text>
                <Text fontSize="sm" mt={2}>위의 "검증" 버튼을 클릭하여 랜덤 검증을 실행하세요.</Text>
              </Box>
            ) : (
              <Box>
                {/* 테이블 */}
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>리포트</Th>
                        <Th isNumeric>질문</Th>
                        <Th>결과(정상, 정보없음, 출력형식, 관련성없음, 오답)</Th>
                        <Th isNumeric>평균신뢰도</Th>
                        <Th isNumeric>응답시간</Th>
                        <Th>생성일</Th>
                        <Th>액션</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {validationResults.map((result) => (
                        <Tr key={result.id} _hover={{ bg: 'gray.50' }}>
                          <Td>
                            <Tooltip label="클릭하여 리포트 보기 (새 창)">
                              <Link
                                href={`/results/${result.result_file}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="blue.500"
                                fontWeight="medium"
                                fontSize="sm"
                                _hover={{ textDecoration: 'underline' }}
                              >
                                {result.result_file}
                              </Link>
                            </Tooltip>
                          </Td>
                          <Td isNumeric>{result.total_questions}</Td>
                          <Td>
                            <HStack spacing={1} flexWrap="wrap">
                              <Tooltip label="정상">
                                <Badge colorScheme="green" size="sm">✅{result.none_count}</Badge>
                              </Tooltip>
                              <Tooltip label="정보없음">
                                <Badge colorScheme="orange" size="sm">🟠{result.no_answer_count}</Badge>
                              </Tooltip>
                              <Tooltip label="출력형식">
                                <Badge colorScheme="yellow" size="sm">🟡{result.format_count}</Badge>
                              </Tooltip>
                              <Tooltip label="관련성없음">
                                <Badge colorScheme="purple" size="sm">🟣{result.irrelevant_count}</Badge>
                              </Tooltip>
                              <Tooltip label="오답">
                                <Badge colorScheme="red" size="sm">🔴{result.wrong_count}</Badge>
                              </Tooltip>
                              {(result.communication_count ?? 0) > 0 && (
                                <Tooltip label="통신오류">
                                  <Badge colorScheme="gray" size="sm">⚫{result.communication_count}</Badge>
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                          <Td isNumeric>
                            <Badge colorScheme={
                              Number(result.avg_confidence) >= 80 ? 'green' :
                              Number(result.avg_confidence) >= 50 ? 'yellow' : 'red'
                            }>
                              {Number(result.avg_confidence).toFixed(1)}%
                            </Badge>
                          </Td>
                          <Td isNumeric>{result.avg_response_time}s</Td>
                          <Td>
                            <Text fontSize="sm">{formatDate(result.created_at)}</Text>
                          </Td>
                          <Td>
                            <IconButton
                              aria-label="삭제"
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDeleteResult(result.id)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                
                {/* 페이징 */}
                <HStack justify="space-between" mt={4} flexWrap="wrap" gap={3}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      페이지당:
                    </Text>
                    <Select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value))
                        setCurrentPage(1)
                      }}
                      size="sm"
                      width="80px"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Select>
                    <Text fontSize="sm" color="gray.500">
                      총 {totalItems}개
                    </Text>
                  </HStack>
                  
                  {totalPages > 1 && (
                    <HStack spacing={1}>
                      {/* 처음 */}
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        isDisabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                        minW="40px"
                      >
                        ««
                      </Button>
                      {/* 이전 */}
                      <IconButton
                        aria-label="이전 페이지"
                        icon={<ChevronLeftIcon />}
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        isDisabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {/* 페이지 번호 */}
                      {getPageNumbers().map((page, index) => {
                        if (page === '...') {
                          return (
                            <Text key={`ellipsis-${index}`} fontSize="sm" px={2} color="gray.500">
                              ...
                            </Text>
                          )
                        }
                        const pageNum = page as number
                        const isActive = pageNum === currentPage
                        return (
                          <Button
                            key={pageNum}
                            size="sm"
                            variant={isActive ? 'solid' : 'outline'}
                            colorScheme="blue"
                            onClick={() => handlePageChange(pageNum)}
                            minW="40px"
                          >
                            {pageNum}
                          </Button>
                        )
                      })}
                      {/* 다음 */}
                      <IconButton
                        aria-label="다음 페이지"
                        icon={<ChevronRightIcon />}
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        isDisabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      {/* 마지막 */}
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        isDisabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        minW="40px"
                      >
                        »»
                      </Button>
                    </HStack>
                  )}
                </HStack>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* 5분류 설명 */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody py={2}>
            <Heading size="sm" mb={2}>📖 검증 분류 설명</Heading>
            <SimpleGrid columns={{ base: 3, md: 6 }} spacing={2}>
              <Box py={1.5} px={2} bg="green.50" borderRadius="md" borderLeft="3px solid" borderColor="green.400">
                <Text fontWeight="bold" color="green.700" fontSize="sm">✅ 정상</Text>
              </Box>
              <Box py={1.5} px={2} bg="orange.50" borderRadius="md" borderLeft="3px solid" borderColor="orange.400">
                <Text fontWeight="bold" color="orange.700" fontSize="sm">🟠 정보없음</Text>
              </Box>
              <Box py={1.5} px={2} bg="yellow.50" borderRadius="md" borderLeft="3px solid" borderColor="yellow.400">
                <Text fontWeight="bold" color="yellow.700" fontSize="sm">🟡 출력형식</Text>
              </Box>
              <Box py={1.5} px={2} bg="purple.50" borderRadius="md" borderLeft="3px solid" borderColor="purple.400">
                <Text fontWeight="bold" color="purple.700" fontSize="sm">🟣 관련성없음</Text>
              </Box>
              <Box py={1.5} px={2} bg="red.50" borderRadius="md" borderLeft="3px solid" borderColor="red.400">
                <Text fontWeight="bold" color="red.700" fontSize="sm">🔴 오답</Text>
              </Box>
              <Box py={1.5} px={2} bg="gray.100" borderRadius="md" borderLeft="3px solid" borderColor="gray.400">
                <Text fontWeight="bold" color="gray.700" fontSize="sm">⚫ 통신오류</Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      </VStack>

      {/* 파일 업로드 확인 모달 */}
      <Modal isOpen={isUploadModalOpen} onClose={onUploadModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>📁 질문 파일 업로드</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {uploadPreview && (
              <VStack spacing={4} align="stretch">
                <Alert status="info" borderRadius="md">
                  <Box>
                    <Text fontWeight="bold">파일: {uploadFile?.name}</Text>
                    <Text fontSize="sm">총 {uploadPreview.total.toLocaleString()}개의 질문이 발견되었습니다.</Text>
                  </Box>
                </Alert>

                <Box>
                  <Text fontWeight="bold" mb={2}>미리보기 (처음 5개)</Text>
                  <Box 
                    bg="gray.50" 
                    p={3} 
                    borderRadius="md" 
                    maxH="300px" 
                    overflowY="auto"
                    fontSize="sm"
                  >
                    {uploadPreview.sample.map((q, idx) => (
                      <Box key={idx} mb={3} pb={2} borderBottom="1px" borderColor="gray.200">
                        <Text fontWeight="bold" color="blue.600">Q: {q.question}</Text>
                        {q.expected_answer && (
                          <Text color="green.600">A: {q.expected_answer}</Text>
                        )}
                        <HStack spacing={2} mt={1}>
                          <Badge colorScheme="purple" size="sm">{q.category || '미분류'}</Badge>
                          {q.source && <Badge colorScheme="gray" size="sm">{q.source}</Badge>}
                        </HStack>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {isUploading && (
                  <Box>
                    <Text fontSize="sm" mb={1}>업로드 진행 중... {uploadProgress}%</Text>
                    <Progress value={uploadProgress} colorScheme="blue" borderRadius="md" />
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={() => {
                onUploadModalClose()
                setUploadFile(null)
                setUploadPreview(null)
              }}
              isDisabled={isUploading}
            >
              취소
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUploadConfirm}
              isLoading={isUploading}
              loadingText="업로드 중..."
            >
              {uploadPreview?.total.toLocaleString()}개 질문 업로드
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default ValidationReportPage
